import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SHIFT_META, ROLE_LABELS, WEEKDAY_CODES, WEEKEND_CODES } from '@/lib/constants';
import { buildDays, weekdayShort, dayNum, isWeekend, isoAddDays } from '@/lib/dates';

export default function ScheduleSheet({ schedule, employees, workplace, onCellChange }) {
  if (!schedule) return null;
  const days = buildDays(schedule.start_date, schedule.num_days);
  const fullDate = (iso) => { const [y, m, d] = iso.split('-'); return `${d}.${m}.${y}`; };
  const assignments = schedule.assignments || {};
  const shiftHoursByCode = Object.fromEntries(((workplace && workplace.shifts) || []).map((s) => [s.code, s.hours]));
  const restWeeks = {};
  (schedule.stats?.per_employee || []).forEach((e) => {
    restWeeks[e.id] = new Set((e.weeks || []).filter((w) => w.full_week && !w.rest_ok).map((w) => w.week_start));
  });
  const hoursById = {};
  (schedule.stats?.per_employee || []).forEach((e) => { hoursById[e.id] = e.total_hours; });
  const compRest = schedule.stats?.comp_rest || {};
  const compRestReason = schedule.stats?.comp_rest_reason || {};

  const weekStartOf = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return isoAddDays(iso, -((dt.getUTCDay() + 6) % 7));
  };

  return (
    <div data-testid="schedule-sheet" className="overflow-x-auto border border-slate-200 rounded-md bg-white w-full">
      <table className="border-collapse text-center">
        <thead>
          <tr>
            <th className="bg-slate-100 border border-slate-200 p-1.5 text-[11px] font-semibold uppercase text-slate-500 sticky left-0 top-0 z-30 text-left w-[118px]">
              Dátum
            </th>
            {employees.map((emp) => {
              const isFlex = !emp.is_virtual && (emp.weekly_hours === 0 || emp.weekly_hours === '0');
              const isBrig = emp.role === 'brigadnik';
              return (
                <th key={emp.id} className={`border border-slate-200 align-bottom sticky top-0 z-10 px-0 py-1.5 w-[46px] ${isBrig ? 'bg-orange-100 border-l-2 border-l-orange-400' : (emp.is_virtual ? 'bg-amber-50' : (isFlex ? 'bg-teal-50/60' : 'bg-slate-50'))}`}>
                  <div className="flex flex-col items-center gap-1">
                    {isBrig && <span data-testid={`brig-badge-${emp.id}`} className="text-[7px] font-bold uppercase text-orange-700 bg-orange-200 rounded px-1 leading-tight">brig</span>}
                    <div className="h-[104px] flex items-end justify-center">
                      <span
                        className={`text-[11px] font-medium whitespace-nowrap leading-none ${isBrig ? 'text-orange-800' : 'text-slate-800'}`}
                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                        title={emp.name}
                      >
                        {emp.name}
                      </span>
                    </div>
                    <span className={`text-[8px] leading-none pb-0.5 ${isBrig ? 'text-orange-500' : 'text-slate-400'}`} title={ROLE_LABELS[emp.role] || emp.role}>
                      {(emp.employment_type_name || ROLE_LABELS[emp.role] || emp.role || '').slice(0, 4)}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {days.map((iso) => {
            const we = isWeekend(iso);
            return (
              <tr key={iso} className={we ? 'bg-fuchsia-50/50' : ''}>
                <td className={`border border-slate-200 sticky left-0 z-20 text-left px-2 py-0.5 shadow-[1px_0_0_0_#e2e8f0] w-[118px] whitespace-nowrap ${we ? 'bg-fuchsia-100/70 font-semibold' : 'bg-white'}`}>
                  <span className="text-[11px] text-slate-700">
                    <span className="uppercase text-slate-400 mr-1">{weekdayShort(iso)}</span>{fullDate(iso)}
                  </span>
                </td>
                {employees.map((emp) => {
                  const isBrig = emp.role === 'brigadnik';
                  const code = (assignments[emp.id] || {})[iso] || 'X';
                  const isNV = code === 'X' && (compRest[emp.id] || []).includes(iso);
                  const meta = isNV ? { bg: '#DCFCE7', text: '#15803D', short: 'NV' } : (SHIFT_META[code] || SHIFT_META.X);
                  const codes = we ? WEEKEND_CODES : WEEKDAY_CODES;
                  const violate = code !== 'X' && code !== 'D' && restWeeks[emp.id]?.has(weekStartOf(iso));
                  const nvReason = isNV ? (compRestReason[emp.id]?.[iso] || '') : '';
                  const nvShort = nvReason.includes('minul') ? 'z min.'
                    : nvReason.replace('za sobotu ', 'So ').replace('za nedeľu ', 'Ne ');
                  return (
                    <td key={emp.id} className={`border border-slate-200 p-0 ${isBrig ? 'border-l-2 border-l-orange-400' : ''}`}>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            data-testid={`sheet-cell-${emp.id}-${iso}`}
                            className="w-full h-8 flex flex-col items-center justify-center font-semibold relative hover:ring-2 hover:ring-inset hover:ring-slate-400 transition-all"
                            style={{ backgroundColor: meta.bg, color: meta.text }}
                            title={isNV ? `Náhradné voľno (§93) za odpracovaný víkend: ${nvReason}` : undefined}
                          >
                            <span className="leading-none text-[11px]">{meta.short}</span>
                            {isNV
                              ? <span className="text-[7px] font-medium opacity-90 leading-none mt-0.5 px-0.5 truncate max-w-full">{nvShort}</span>
                              : (shiftHoursByCode[code] ? <span className="text-[7px] font-normal opacity-70 leading-none mt-0.5">{shiftHoursByCode[code]}h</span> : null)}
                            {violate && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" title="§93 odpočinok" />}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2" align="center">
                          <div className="text-xs text-slate-500 mb-2 px-1">{emp.name} · {dayNum(iso)}. {weekdayShort(iso)}</div>
                          <div className="grid grid-cols-3 gap-1">
                            {codes.map((c) => {
                              const m = SHIFT_META[c];
                              return (
                                <button
                                  key={c}
                                  data-testid={`sheet-cell-opt-${emp.id}-${iso}-${c}`}
                                  onClick={() => onCellChange(emp.id, iso, c)}
                                  className="px-2 py-1.5 rounded text-xs font-medium border hover:opacity-80 transition-opacity"
                                  style={{ backgroundColor: m.bg, color: m.text, borderColor: m.border }}
                                >
                                  {m.short || '—'}
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr data-testid="sheet-footer-hours">
            <td className="border border-slate-200 sticky left-0 z-20 bg-slate-100 text-left px-2 py-1 text-[10px] font-semibold text-slate-600 shadow-[1px_0_0_0_#e2e8f0]">Odpracované</td>
            {employees.map((emp) => (
              <td key={emp.id} className={`border border-slate-200 text-center font-mono text-[10px] font-semibold bg-slate-50 px-0.5 ${emp.role === 'brigadnik' ? 'border-l-2 border-l-orange-400 text-orange-700' : 'text-slate-700'}`}>{hoursById[emp.id] ?? 0}h</td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
